import React from 'react';
import { ArrowRightIcon, CheckIcon, CalendarIcon, UsersIcon, MessageCircleIcon, ZapIcon, UserIcon } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function Coaching() {
  const {
    t
  } = useLanguage();
  const signs = t('coaching.signs') as string[];
  const whatWeDoItems = t('coaching.whatWeDoItems') as string[];
  const relatedChallenges = [{
    icon: <UsersIcon size={22} className="text-coral" data-id="element-1463" data-mp="w7or6fw659q5" />,
    titleKey: 'coaching.rel1Title',
    descKey: 'coaching.rel1Desc',
    path: '/services/new-to-leadership',
    color: 'border-coral/20'
  }, {
    icon: <MessageCircleIcon size={22} className="text-teal" data-id="element-1464" data-mp="27nddr65ig6qf" />,
    titleKey: 'coaching.rel2Title',
    descKey: 'coaching.rel2Desc',
    path: '/services/conflict-and-conversations',
    color: 'border-teal/20'
  }, {
    icon: <ZapIcon size={22} className="text-blue-600" data-id="element-1465" data-mp="pjxwkzj7m23c" />,
    titleKey: 'coaching.rel3Title',
    descKey: 'coaching.rel3Desc',
    path: '/services/leading-in-complexity',
    color: 'border-blue-200'
  }];
  return <PageTransition data-id="element-1466" data-mp="gllyydjxe41e">
      <div className="pt-12 pb-24" data-id="element-1467" data-mp="gm5ifyp850hj">
        {/* Hero */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1468" data-mp="bsazwylcurk1">
          <DecorativeShape type="blob" color="bg-coral/10" className="top-0 right-0 w-[500px] h-[500px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-1469" data-mp="6tg8j89bnuob" />
          <Animate className="max-w-3xl" data-id="element-1470" data-mp="3t1za9pbcy71f">
            <span className="inline-block px-4 py-2 bg-coral/10 text-coral font-medium rounded-full text-sm mb-6" data-id="element-1471" data-mp="3t0xwve6vd8gj">
              {t('coaching.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-1472" data-mp="3eehclaw7qcoi">
              {t('coaching.title')}
              <br data-id="element-1473" data-mp="3jemz6cnml8zq" />
              <span className="text-coral" data-id="element-1474" data-mp="3jf56c4btt7mf">{t('coaching.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-600 mb-6 leading-relaxed" data-id="element-1475" data-mp="35fx1auszf5ab">
              {t('coaching.intro1')}
            </p>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1476" data-mp="3af06qgcvjdit">
              {t('coaching.intro2')}
            </p>
            <Link to="/contact" data-id="element-1477" data-mp="5zgvtd2faqg3">
              <Button size="lg" rightIcon={<ArrowRightIcon size={20} data-id="element-1479" data-mp="611vfh3lgzkt" />} data-id="element-1478" data-mp="5xnsa6n3wmcb">
                {t('coaching.heroCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        {/* Signs section */}
        <section className="bg-cream py-20 mb-20" data-id="element-1480" data-mp="3t9x355xl3qg5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1481" data-mp="kvy600yvlz12">
            <Animate className="mb-12" data-id="element-1482" data-mp="pmy5ndzdqv79">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-1483" data-mp="pnzq292acu1n">
                {t('coaching.signsTitle')}
              </h2>
            </Animate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1484" data-mp="yzdpmbmcp6uq">
              {signs.map((sign, i) => <Animate key={i} delay={i * 0.05} data-id="element-1485" data-mp="18rjom5b0sojf" data-mpi={i}>
                  <div className="flex items-start gap-3 bg-white rounded-2xl p-5 shadow-sm" data-id="element-1486" data-mp="18q8elnqzekjv">
                    <div className="mt-0.5 p-1 rounded-full bg-coral/10 text-coral flex-shrink-0" data-id="element-1487" data-mp="13trlsg8u7i0g">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1488" data-mp="g3nlahqy3ydo" />
                    </div>
                    <span className="text-gray-700" data-id="element-1489" data-mp="c2c512tyopi5">{sign}</span>
                  </div>
                </Animate>)}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1490" data-mp="l17audt3fxkx">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-1491" data-mp="21fp8yqy5wbiq">
            <Animate data-id="element-1492" data-mp="1whe2mq72sj59">
              <h2 className="text-3xl font-bold text-charcoal mb-6" data-id="element-1493" data-mp="1whx7p6bmnd1v">
                {t('coaching.whatWeDoTitle')}
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-1494" data-mp="61lc42e82y3l">
                {t('coaching.whatWeDoP1')}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1495" data-mp="fy7tuib9591e">
                {t('coaching.whatWeDoP2')}
              </p>
            </Animate>
            <Animate delay={0.1} data-id="element-1496" data-mp="k0kw2wqsiotf">
              <div className="space-y-3" data-id="element-1497" data-mp="jxglnhmqewub">
                {whatWeDoItems.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-1498" data-mp="ows54yehggfl" data-mpi={i}>
                    <div className="mt-1 p-1 rounded-full bg-coral/10 text-coral flex-shrink-0" data-id="element-1499" data-mp="two7yubg21tk">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1500" data-mp="yq0jq3v0ye4w" />
                    </div>
                    <span className="text-gray-700" data-id="element-1501" data-mp="13mqzsr6tx9qb">{item}</span>
                  </div>)}
              </div>
            </Animate>
          </div>
        </section>

        {/* What it typically covers */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1502" data-mp="1cp095dqd2x24">
          <Animate className="mb-10" data-id="element-1503" data-mp="2x7kk8zggp4c5">
            <h2 className="text-2xl font-bold text-charcoal" data-id="element-1504" data-mp="2x4eudi00dyfd">
              {t('coaching.relatedTitle')}
            </h2>
            <p className="text-gray-500 mt-1" data-id="element-1505" data-mp="2o5tg3z1kl5ke">{t('coaching.relatedSubtitle')}</p>
          </Animate>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1506" data-mp="2j6qaodhogxbw">
            {relatedChallenges.map((ch, i) => <Animate key={i} delay={i * 0.1} data-id="element-1507" data-mp="3fxirxegckvf9" data-mpi={i}>
                <Link to={ch.path} className="block group h-full" data-id="element-1508" data-mp="3fw7mzbb61hmk">
                  <Card className={`h-full p-6 border-2 ${ch.color} hover:shadow-md transition-all`} hoverEffect data-id="element-1509" data-mp="3bwgpypv1q59l">
                    <div className="flex items-center gap-3 mb-3" data-id="element-1510" data-mp="373e3t2slly3f">
                      {ch.icon}
                      <h3 className="font-bold text-charcoal" data-id="element-1511" data-mp="324t18wpvj2bt">{t(ch.titleKey)}</h3>
                    </div>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4" data-id="element-1512" data-mp="3h5cprlso89y">{t(ch.descKey)}</p>
                    <div className="flex items-center text-sm font-bold text-coral" data-id="element-1513" data-mp="3usp0a82x16br">
                      {t('coaching.learnMore')}
                      <ArrowRightIcon size={14} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1514" data-mp="mk7jp4itacq5" />
                    </div>
                  </Card>
                </Link>
              </Animate>)}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-1515" data-mp="csl28qfvrhey">
          <Animate data-id="element-1516" data-mp="3uyzbwd74957j">
            <Card className="p-12 bg-gradient-to-br from-coral/5 to-orange-50 border-0" data-id="element-1517" data-mp="3uyfv4zt7cmv3">
              <div className="w-16 h-16 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-md" data-id="element-1518" data-mp="3uir3i2fn8dy">
                <CalendarIcon size={32} className="text-coral" data-id="element-1519" data-mp="3m15l1hs4f3rr" />
              </div>
              <h2 className="text-3xl font-bold mb-4" data-id="element-1520" data-mp="3q1fy1eebyug4">{t('coaching.ctaTitle')}</h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-1521" data-mp="1k90vgcy3gik7">{t('coaching.ctaDesc')}</p>
              <Link to="/contact" data-id="element-1522" data-mp="1o8jet4a9olkx">
                <Button variant="primary" rightIcon={<ArrowRightIcon size={18} data-id="element-1524" data-mp="38rc0uccxjo3i" />} data-id="element-1523" data-mp="1o8rqisunlkuj">
                  {t('coaching.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}